import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  createHashRouter,
  RouterProvider,
  useNavigate,
} from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/react";
import "@fontsource-variable/ibm-plex-sans";
import "@fontsource-variable/source-serif-4";
import "@fontsource/ibm-plex-mono/500.css";

import { App, ConfigurationRequired } from "./App";
import { clerkAllowedRedirectOrigins } from "./clerk-host-routing";
import { isDesktopShell } from "./desktop";
import { AppErrorBoundary } from "./identity-boot";
import { applyResolvedColorTheme, watchColorTheme } from "./theme";
import "./styles.css";

applyResolvedColorTheme();
watchColorTheme(() => undefined);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30_000,
    },
  },
});

const rootElement = document.querySelector<HTMLDivElement>("#root");
if (!rootElement) {
  throw new Error("Missing #root element");
}

const rawPublishableKey = (import.meta.env as Record<string, unknown>)[
  "VITE_CLERK_PUBLISHABLE_KEY"
];
const normalizedPublishableKey =
  typeof rawPublishableKey === "string" ? rawPublishableKey.trim() : "";
const publishableKey =
  normalizedPublishableKey.length > 0 &&
  !normalizedPublishableKey.startsWith("replace-")
    ? normalizedPublishableKey
    : null;
const desktop = isDesktopShell();
const rawApiBaseUrl = (import.meta.env as Record<string, unknown>)[
  "VITE_API_BASE_URL"
];
const hasDesktopApiBaseUrl =
  typeof rawApiBaseUrl === "string" && rawApiBaseUrl.trim().length > 0;
const createRouter = desktop ? createHashRouter : createBrowserRouter;
const desktopConfigured = !desktop || hasDesktopApiBaseUrl;
const desktopRedirectOrigins = clerkAllowedRedirectOrigins(desktop);

function ClerkApp({
  publishableKey: key,
  children,
}: {
  readonly publishableKey: string;
  readonly children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={key}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/sign-in"
      {...(desktopRedirectOrigins === undefined
        ? {}
        : { allowedRedirectOrigins: [...desktopRedirectOrigins] })}
    >
      {children}
    </ClerkProvider>
  );
}

const router =
  publishableKey !== null && desktopConfigured
    ? createRouter([
        {
          path: "*",
          element: (
            <ClerkApp publishableKey={publishableKey}>
              <App />
            </ClerkApp>
          ),
        },
      ])
    : null;

createRoot(rootElement).render(
  <StrictMode>
    <AppErrorBoundary>
      {router !== null ? (
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      ) : (
        <ConfigurationRequired />
      )}
    </AppErrorBoundary>
  </StrictMode>,
);
